import React, { useState, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { markDocumentAsCompleted } from '../../../../services/userService';

const CourseDocument = ({ documentData, documentUrl, onDocumentComplete, currentLessonIndex, totalLessons, onNextLesson }) => {
  const [loadError, setLoadError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isSavingComplete, setIsSavingComplete] = useState(false);
  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const fileUrl = documentData?.url || documentUrl || '';
  const title = documentData?.title || 'Document';
  const description = documentData?.description || '';

  const isPdf = typeof fileUrl === 'string' && (fileUrl.toLowerCase().endsWith('.pdf') || fileUrl.includes('pdf'));

  const handleScroll = () => {
    // Removed auto-complete on scroll
  };

  const markAsComplete = async () => {
    if (isSavingComplete || isCompleted || !documentData?.id) return;
    
    try {
      setIsSavingComplete(true);
      setIsCompleted(true);
      await markDocumentAsCompleted({ 
        document_id: documentData.id
      });
      
      if (onDocumentComplete) {
        onDocumentComplete(documentData.id);
      }
    } catch (error) {
      console.error('Error marking document as complete:', error);
      setIsCompleted(false);
      toast.error('Failed to mark document as complete. Please try again.', { position: 'top-center' });
    } finally {
      setIsSavingComplete(false);
    }
  };

  const handleMarkComplete = () => {
    markAsComplete();
  };

  useEffect(() => {
    setLoadError(false);
    setIsLoading(true);
    setIsCompleted(documentData?.completed === 1);
  }, [fileUrl, documentData]);

  const handleIframeLoad = () => {
    setIsLoading(false);
    setLoadError(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setLoadError(true);
  };

  const handleDownload = () => {
    try {
      if (!fileUrl) return;
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = title || 'document';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      toast.error('Unable to download this document.', { position: 'top-center' });
    }
  };

  const toggleFullscreen = () => {
    try {
      if (!document.fullscreenElement) {
        containerRef.current?.requestFullscreen();
        setIsFullscreen(true);
      } else {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (e) {
      toast.error('Fullscreen is not available in this browser.', { position: 'top-center' });
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  if (!fileUrl) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] py-16 px-6 text-center bg-gray-50 rounded-2xl">
        <span className="flex items-center justify-center w-20 h-20 rounded-2xl bg-white border border-gray-200 text-gray-400 mb-5 shadow-sm">
          <Icon icon="mdi:file-document-outline" width="40" height="40" />
        </span>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No document available</h3>
        <p className="text-sm text-gray-600 max-w-sm">This item does not have a document attached or the link is invalid.</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`flex flex-col ${isFullscreen ? 'h-full' : 'h-[calc(100vh-220px)] min-h-[520px]'}`}
    >
      {/* Document header */}
      <div className="shrink-0 px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 mb-1">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 text-xs font-semibold">
                <Icon icon="mdi:file-document-outline" width="14" height="14" />
                {isPdf ? 'PDF Document' : 'Document'}
              </span>
            </div>
            <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
            {description && <p className="mt-1 text-sm text-gray-600 line-clamp-1">{description}</p>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={handleDownload}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <Icon icon="mdi:download" width="18" height="18" />
              Download
            </button>
            <button
              type="button"
              onClick={toggleFullscreen}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#7083FF] hover:bg-[#5f73e6] rounded-lg transition-colors"
            >
              <Icon icon={isFullscreen ? "mdi:fullscreen-exit" : "mdi:fullscreen"} width="18" height="18" />
              {isFullscreen ? 'Exit' : 'Fullscreen'}
            </button>
          </div>
        </div>
      </div>

      {/* Viewer area */}
      <div 
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="relative bg-gray-50 overflow-hidden flex-1 min-h-0"
      >
        {isLoading && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-[#7083FF] border-t-transparent" />
            <p className="mt-4 text-sm font-medium text-gray-700">Loading document...</p>
          </div>
        )}

        {loadError ? (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-8 text-center bg-white">
            <span className="flex items-center justify-center w-20 h-20 rounded-2xl bg-red-50 text-red-600 mb-5">
              <Icon icon="mdi:file-document-alert-outline" width="40" height="40" />
            </span>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to display document</h3>
            <p className="text-sm text-gray-600 mb-6 max-w-md">
              This document cannot be displayed in the browser. Please download it to view.
            </p>
            <button
              type="button"
              onClick={handleDownload}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-[#7083FF] hover:bg-[#5f73e6] rounded-lg transition-colors"
            >
              <Icon icon="mdi:download" width="18" height="18" />
              Download Document
            </button>
          </div>
        ) : (
          <iframe
            ref={iframeRef}
            src={isPdf ? `${fileUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH` : `https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`}
            title={title}
            className="w-full h-full border-0"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
          />
        )}
      </div>

      {/* Bottom action bar */}
      <div className="shrink-0 px-6 py-4 border-t border-gray-200 bg-white">
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm text-gray-500">Lesson <span className="font-semibold text-gray-900">{currentLessonIndex}</span> of {totalLessons}</span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleMarkComplete}
              disabled={isCompleted || isSavingComplete}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSavingComplete ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Icon icon="mdi:check-circle" width="18" height="18" />
                  {isCompleted ? 'Completed' : 'Mark as Complete'}
                </>
              )}
            </button>
            {isCompleted && typeof onNextLesson === 'function' && totalLessons > 0 && (
              <button
                type="button"
                onClick={onNextLesson}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-[#7083FF] hover:bg-[#5f73e6] transition-colors"
              >
                {currentLessonIndex < totalLessons ? 'Next Lesson' : 'Explore Courses'}
                <Icon icon="mdi:arrow-right" width="18" height="18" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDocument;
